import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { CATEGORIES, TYPE_LABELS } from "../lib/categories";
import CountdownTimer from "../components/CountdownTimer";
import StarRating from "../components/StarRating";
import { MapPin, MessageSquare, Gavel, Trash2, ChevronLeft, ChevronRight, AlertCircle, CreditCard, Star, Share2, Rocket, Copy, Facebook, Check, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function ListingDetail() {
  const { id } = useParams();
  const { lang, t } = useI18n();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [bidAmount, setBidAmount] = useState("");
  const [bids, setBids] = useState([]);
  const [showMessage, setShowMessage] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [showRate, setShowRate] = useState(false);
  const [rateStars, setRateStars] = useState(5);
  const [rateReview, setRateReview] = useState("");
  const [sellerRating, setSellerRating] = useState({ average: 0, count: 0 });

  useEffect(() => {
    api.get(`/listings/${id}`).then((r) => setListing(r.data)).catch(() => navigate("/browse"));
  }, [id, navigate]);

  useEffect(() => {
    if (!listing) return;
    api.get(`/ratings/user/${listing.user_id}`).then((r) => setSellerRating({ average: r.data.average, count: r.data.count })).catch(()=>{});
    if (listing.listing_type === "auction") {
      api.get(`/listings/${id}/bids`).then((r) => setBids(r.data)).catch(()=>{});
    }
  }, [listing, id]);

  if (!listing) return <div className="py-20 text-center text-[#7A7A73]">...</div>;

  const cat = CATEGORIES.find((c) => c.key === listing.category);
  const typeLabel = TYPE_LABELS[listing.listing_type]?.[lang];
  const isOwner = user?.user_id === listing.user_id;

  const sendMessage = async () => {
    if (!user) { login(); return; }
    if (!messageText.trim()) return;
    try {
      await api.post("/messages", {
        listing_id: listing.id,
        recipient_id: listing.user_id,
        text: messageText,
      });
      toast.success(t("send"));
      setMessageText("");
      setShowMessage(false);
      navigate("/messages");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Klaida");
    }
  };

  const placeBid = async () => {
    if (!user) { login(); return; }
    const amt = parseFloat(bidAmount);
    if (!amt || amt <= 0) return toast.error("Įvesk sumą");
    try {
      await api.post(`/listings/${id}/bid`, { amount: amt });
      const r = await api.get(`/listings/${id}`);
      setListing(r.data);
      const b = await api.get(`/listings/${id}/bids`);
      setBids(b.data);
      setBidAmount("");
      toast.success("Pasiūlymas pateiktas!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Klaida");
    }
  };

  const submitRating = async () => {
    if (!user) { login(); return; }
    try {
      await api.post("/ratings", {
        target_user_id: listing.user_id,
        listing_id: listing.id,
        stars: rateStars,
        review: rateReview,
      });
      toast.success(t("rating_thanks"));
      setShowRate(false);
      setRateReview("");
      const r = await api.get(`/ratings/user/${listing.user_id}`);
      setSellerRating({ average: r.data.average, count: r.data.count });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Klaida");
    }
  };

  const deleteListing = async () => {
    if (!window.confirm("Ištrinti?")) return;
    await api.delete(`/listings/${id}`);
    navigate("/profile");
  };

  const payNow = async () => {
    try {
      const co = await api.post("/payments/checkout", { listing_id: listing.id, origin_url: window.location.origin });
      if (co.data.url) { window.location.href = co.data.url; }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Klaida");
    }
  };

  const bumpNow = async () => {
    try {
      const origin = encodeURIComponent(window.location.origin);
      const co = await api.post(`/listings/${listing.id}/bump-checkout?origin_url=${origin}`);
      if (co.data.url) { window.location.href = co.data.url; }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Klaida");
    }
  };

  const shareUrl = `${window.location.origin}/listing/${listing.id}`;
  const shareText = `${listing.title} — ${listing.listing_type === "free" ? "Atiduodu" : `${listing.price.toFixed(2)} €`} · ${listing.city}`;

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: listing.title, text: shareText, url: shareUrl });
      } catch (_e) {/* user cancelled */}
    } else {
      shareCopy();
    }
  };
  const shareFacebook = () => {
    const u = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(u, "_blank", "noopener,width=600,height=600");
  };
  const shareCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Nuoroda nukopijuota");
    } catch (_e) {
      toast.error("Nepavyko nukopijuoti");
    }
  };

  const tagClass = {
    sell: "tag-sell", free: "tag-free", trade: "tag-trade", auction: "tag-auction",
  }[listing.listing_type];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Gallery */}
        <div>
          <div className="aspect-square bg-[#F0EFEB] border border-[#D1CFC7] rounded-md overflow-hidden relative" data-testid="listing-gallery">
            {listing.images?.length > 0 ? (
              <>
                <img src={fileUrl(listing.images[imgIdx])} alt={listing.title} className="w-full h-full object-cover"/>
                {listing.images.length > 1 && (
                  <>
                    <button
                      data-testid="gallery-prev"
                      onClick={() => setImgIdx((imgIdx - 1 + listing.images.length) % listing.images.length)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/70 text-white rounded-full p-2 hover:bg-[#C85A32]"
                    ><ChevronLeft className="w-5 h-5"/></button>
                    <button
                      data-testid="gallery-next"
                      onClick={() => setImgIdx((imgIdx + 1) % listing.images.length)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/70 text-white rounded-full p-2 hover:bg-[#C85A32]"
                    ><ChevronRight className="w-5 h-5"/></button>
                  </>
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#7A7A73]">
                {cat?.icon && <cat.icon className="w-16 h-16"/>}
              </div>
            )}
          </div>
          {listing.images?.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto">
              {listing.images.map((img, idx) => (
                <button
                  key={idx}
                  data-testid={`gallery-thumb-${idx}`}
                  onClick={() => setImgIdx(idx)}
                  className={`flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 ${imgIdx === idx ? "border-[#C85A32]" : "border-transparent"}`}
                >
                  <img src={fileUrl(img)} alt="" className="w-full h-full object-cover"/>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="flex items-start gap-2 flex-wrap">
            <span className={`tag ${tagClass}`}>{typeLabel}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-black tracking-tight text-[#1A1A18] mt-3">
            {listing.title}
          </h1>

          {/* Share row */}
          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="share-row">
            <button
              data-testid="share-native"
              onClick={shareNative}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-[#D1CFC7] rounded-full hover:bg-[#1A1A18] hover:text-white hover:border-[#1A1A18] transition-colors"
            >
              <Share2 className="w-3.5 h-3.5"/> Dalintis
            </button>
            <button
              data-testid="share-facebook"
              onClick={shareFacebook}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-[#D1CFC7] rounded-full hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2] transition-colors"
            >
              <Facebook className="w-3.5 h-3.5"/> Facebook
            </button>
            <button
              data-testid="share-copy"
              onClick={shareCopy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-[#D1CFC7] rounded-full hover:bg-[#1A1A18] hover:text-white hover:border-[#1A1A18] transition-colors"
            >
              <Copy className="w-3.5 h-3.5"/> Kopijuoti nuorodą
            </button>
          </div>

          {listing.listing_type === "auction" ? (
            <div className="mt-4 bg-[#1A1A18] text-white p-4 rounded-md">
              <div className="flex items-center gap-2 text-[#DDA74F]">
                <Gavel className="w-5 h-5"/>
                <span className="font-heading font-semibold">{t("auction_ends")}</span>
              </div>
              <div className="mt-2"><CountdownTimer endAt={listing.auction_end_at}/></div>
              <div className="mt-3 text-2xl font-heading font-black">
                {(listing.highest_bid ?? listing.price).toFixed(2)} €
              </div>
              <div className="text-xs text-white/60">
                {listing.highest_bid ? `${t("current_bid")}: ${listing.highest_bidder_name}` : t("starting_price")}
              </div>
            </div>
          ) : (
            <div className="mt-3 text-3xl font-heading font-black text-[#1A1A18]">
              {listing.listing_type === "free" ? t("free") : `${listing.price.toFixed(2)} €`}
            </div>
          )}

          <div className="mt-4 flex items-center gap-3 text-sm text-[#4A4A45]">
            <MapPin className="w-4 h-4"/>{listing.city}
            <span className="text-[#D1CFC7]">·</span>
            <span>{cat?.[lang]}</span>
          </div>

          <p className="mt-6 text-[#1A1A18] whitespace-pre-wrap leading-relaxed">{listing.description}</p>

          {/* Seller card */}
          <div className="mt-6 p-4 border border-[#D1CFC7] rounded-md flex items-center gap-3" data-testid="seller-card">
            {listing.user_picture ? (
              <img src={listing.user_picture} alt="" className="w-12 h-12 rounded-full"/>
            ) : (
              <div className="w-12 h-12 rounded-full bg-[#1A1A18] text-white flex items-center justify-center font-bold">
                {listing.user_name?.[0]}
              </div>
            )}
            <div className="flex-1">
              <p className="font-heading font-bold text-[#1A1A18]">{listing.user_name}</p>
              {sellerRating.count > 0 ? (
                <div className="flex items-center gap-2 mt-0.5">
                  <StarRating value={sellerRating.average} size={14} readOnly testId="seller-rating-display" />
                  <span className="text-xs text-[#7A7A73]">{sellerRating.average} ({sellerRating.count})</span>
                </div>
              ) : (
                <span className="text-xs text-[#7A7A73]">No ratings yet</span>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-5 space-y-3">
            {isOwner ? (
              <>
                <button
                  data-testid="edit-listing-button"
                  onClick={() => navigate(`/post/${listing.id}`)}
                  className="w-full btn-secondary inline-flex items-center justify-center gap-2"
                >
                  <Pencil className="w-4 h-4"/> {t("edit_listing")}
                </button>
                {/* Bump and Pay features temporarily disabled — monetization will come via Google Play / App Store */}
                <button
                  data-testid="delete-listing-button"
                  onClick={deleteListing}
                  className="w-full text-sm text-[#C85A32] hover:underline py-2 inline-flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-4 h-4"/> {t("delete")}
                </button>
              </>
            ) : (
              <>
                {listing.listing_type === "auction" && (
                  <div className="flex gap-2">
                    <input
                      data-testid="bid-input"
                      type="number"
                      step="0.01"
                      min={(listing.highest_bid ?? listing.price) + 0.5}
                      value={bidAmount}
                      onChange={(e) => setBidAmount(e.target.value)}
                      placeholder={`> ${(listing.highest_bid ?? listing.price).toFixed(2)} €`}
                      className="flex-1 bg-white border border-[#D1CFC7] rounded-md px-4 py-3 outline-none focus:ring-2 focus:ring-[#C85A32]"
                    />
                    <button data-testid="bid-button" onClick={placeBid} className="btn-primary">
                      <Gavel className="w-4 h-4"/>
                    </button>
                  </div>
                )}
                <button
                  data-testid="contact-seller-button"
                  onClick={() => user ? setShowMessage(!showMessage) : login()}
                  className="w-full btn-primary flex items-center justify-center gap-2"
                >
                  <MessageSquare className="w-4 h-4"/> {t("contact_seller")}
                </button>
                {showMessage && (
                  <div className="space-y-2" data-testid="message-form">
                    <textarea
                      data-testid="message-text"
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      placeholder={t("type_message")}
                      rows={3}
                      className="w-full bg-white border border-[#D1CFC7] rounded-md px-4 py-2 outline-none focus:ring-2 focus:ring-[#C85A32]"
                    />
                    <button data-testid="message-send" onClick={sendMessage} className="btn-secondary w-full">
                      {t("send")}
                    </button>
                  </div>
                )}
                <button
                  data-testid="rate-toggle-button"
                  onClick={() => user ? setShowRate(!showRate) : login()}
                  className="w-full text-sm font-semibold text-[#4A4A45] hover:text-[#1A1A18] py-2 inline-flex items-center justify-center gap-2"
                >
                  <Star className="w-4 h-4"/> {t("rate_user")}
                </button>
                {showRate && (
                  <div className="border border-[#D1CFC7] rounded-md p-4 space-y-3" data-testid="rate-form">
                    <StarRating value={rateStars} onChange={setRateStars} testId="rate-stars-input" />
                    <textarea
                      data-testid="rate-review"
                      value={rateReview}
                      onChange={(e) => setRateReview(e.target.value)}
                      placeholder={t("write_review")}
                      rows={2}
                      className="w-full bg-white border border-[#D1CFC7] rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-[#C85A32]"
                    />
                    <button data-testid="rate-submit" onClick={submitRating} className="btn-primary w-full">
                      {t("submit_rating")}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {listing.listing_type === "auction" && bids.length > 0 && (
            <div className="mt-6 border border-[#D1CFC7] rounded-md p-4">
              <p className="font-heading font-bold text-sm uppercase tracking-widest text-[#7A7A73] mb-3">Bids</p>
              <div className="space-y-2">
                {bids.slice(0, 5).map((b) => (
                  <div key={b.id} className="flex items-center justify-between text-sm">
                    <span className="text-[#1A1A18]">{b.bidder_name}</span>
                    <span className="font-bold text-[#C85A32]">{b.amount.toFixed(2)} €</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
